import { baseApi } from "./baseApi";
import type { Product, CreateProductData, UpdateProductData } from "../types/product";

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ProductFilters {
  categoryId?: string;
  featured?: boolean;
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  page?: number;
  limit?: number;
}

export interface PaginatedProductsResponse {
  success: boolean;
  data: Product[];
  pagination: PaginationMeta;
  message?: string;
}

/**
 * Products API slice
 * Handles all product-related API calls
 */
export const productsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all products with pagination
    getProducts: builder.query<PaginatedProductsResponse, ProductFilters | void>({
      query: (params) => {
        if (!params) return "/products";
        const searchParams = new URLSearchParams();
        if (params.categoryId) searchParams.append("categoryId", params.categoryId);
        if (params.featured !== undefined) searchParams.append("featured", String(params.featured));
        if (params.search) searchParams.append("search", params.search);
        if (params.minPrice !== undefined) searchParams.append("minPrice", String(params.minPrice));
        if (params.maxPrice !== undefined) searchParams.append("maxPrice", String(params.maxPrice));
        if (params.inStock !== undefined) searchParams.append("inStock", String(params.inStock));
        if (params.page !== undefined) searchParams.append("page", String(params.page));
        if (params.limit !== undefined) searchParams.append("limit", String(params.limit));
        const queryString = searchParams.toString();
        return `/products${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["Product"],
    }),

    // Get product by ID
    getProductById: builder.query<
      { success: boolean; data: Product; message?: string },
      string
    >({
      query: (id) => `/products/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Product", id }],
    }),

    // Create product (Admin only)
    createProduct: builder.mutation<
      { success: boolean; data: Product; message?: string },
      CreateProductData
    >({
      query: (data) => ({
        url: "/products",
        method: "POST",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["Product"],
    }),

    // Update product (Admin only)
    updateProduct: builder.mutation<
      { success: boolean; data: Product; message?: string },
      { id: string; data: UpdateProductData }
    >({
      query: ({ id, data }) => ({
        url: `/products/${id}`,
        method: "PUT",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Product", id }, "Product"],
    }),

    // Delete product (Admin only)
    deleteProduct: builder.mutation<
      { success: boolean; message?: string },
      string
    >({
      query: (id) => ({
        url: `/products/${id}`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: ["Product"],
    }),

    // Get deleted products (Admin only)
    getDeletedProducts: builder.query<PaginatedProductsResponse, ProductFilters | void>({
      query: (params) => {
        if (!params) return "/products/admin/deleted";
        const searchParams = new URLSearchParams();
        if (params.categoryId) searchParams.append("categoryId", params.categoryId);
        if (params.search) searchParams.append("search", params.search);
        if (params.page !== undefined) searchParams.append("page", String(params.page));
        if (params.limit !== undefined) searchParams.append("limit", String(params.limit));
        const queryString = searchParams.toString();
        return `/products/admin/deleted${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["Product"],
    }),

    // Restore product (Admin only)
    restoreProduct: builder.mutation<
      { success: boolean; data: Product; message?: string },
      string
    >({
      query: (id) => ({
        url: `/products/${id}/restore`,
        method: "PUT",
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "Product", id }, "Product"],
    }),

    // Reorder products (Admin only)
    reorderProducts: builder.mutation<
      { success: boolean; message?: string },
      { items: { id: string; order: number }[] }
    >({
      query: (data) => ({
        url: "/products/admin/reorder",
        method: "PATCH",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["Product"],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useGetDeletedProductsQuery,
  useRestoreProductMutation,
  useReorderProductsMutation,
} = productsApi;

